import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  Paper,
  LinearProgress,
  alpha,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Avatar,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  Reply as ReplyIcon,
  Info as InfoIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  Send as SendIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Gavel as GavelIcon,
} from "@mui/icons-material";
import {
  decryptText,
  decryptAppId,
} from "../../../utils/encryption";
import s3Utils from '../../../utils/s3Utils';
import PageTitle from '../../../components/PageTitle';

// Modern color palette
const colors = {
  primary: {
    main: "#6366f1",
    light: "#818cf8",
    dark: "#4f46e5",
  },
  secondary: {
    main: "#ec4899",
    light: "#f472b6",
    dark: "#db2777",
  },
  success: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
  },
  grey: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
};

const DoNCReply = () => {
  const location = useLocation();

  // Function to get parameters from URL
  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get("orgId");
    if (encryptedOrgId) {
      try {
        return decryptText(encryptedOrgId);
      } catch (error) {
        return encryptedOrgId;
      }
    }
    return null;
  };

  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        return decryptAppId(encryptedAppId);
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        return decryptText(encryptedAssessmentId);
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return null;
  };

  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        return decryptText(encryptedAssessorId);
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  // State management
  const [loading, setLoading] = useState(true);
  const [orgId] = useState(getOrgIdFromQuery());
  const [appId] = useState(getAppIdFromQuery());
  const [assessmentId] = useState(getAssessmentIdFromQuery());
  const [assessorId] = useState(getAssessorIdFromQuery());
  const [nonConformities, setNonConformities] = useState([]);
  const [loadingNC, setLoadingNC] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");

  // Reply functionality state
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedNcForReply, setSelectedNcForReply] = useState(null);
  const [replyRecipient, setReplyRecipient] = useState(""); // "Assessor" or "Lab"
  const [replyRemarks, setReplyRemarks] = useState("");
  const [replyAction, setReplyAction] = useState(""); // "reply" or "close"
  const [sendingReply, setSendingReply] = useState(false);
  const [closingNC, setClosingNC] = useState({});

  // Send to Lab functionality state
  const [sendingToLab, setSendingToLab] = useState(false);

  // Close NC Dialog state
  const [closeNCDialogOpen, setCloseNCDialogOpen] = useState(false);
  const [selectedNCForClose, setSelectedNCForClose] = useState(null);
  const [closeNCRemarks, setCloseNCRemarks] = useState("");

  // Show snackbar message
  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Fetch Non-Conformities data
  const fetchNonConformities = useCallback(async () => {
    if (!assessmentId || !appId) {
      console.warn("Missing assessmentId or appId for NC fetch");
      setLoading(false);
      return;
    }

    setLoadingNC(true);
    try {   

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/nc-main/GetNCMainall-with-closed`,
        {
          params: {
            assessmentId: assessmentId,
            appId: appId,
          },
        }
      );

      if (response.data.isstatus) {
        const ncData = response.data.nc || [];

        // Format NC data for better handling
        const formattedNC = ncData.map((item) => ({
          id: item.id,
          clause: item.clauseno || "N/A",
          description: item.description || "",
          nonconformity: item.nonconformity || "",
          evidence: item.evidence || "",
          requirement: item.requirement || "",
          document: item.document || "",
          ncid: item.ncid,
          assessorid: item.assessorid || "",
          cabReply: item.cabReply || "",
          cabReplyDate: item.cabReplyDate || null,
          isLabAccept: item.isLabAccept,
          fileupload: item.fileupload || "",
          assessorRemarks: item.assessorRemarks || "",
          assessorRemarksBy: item.assessorRemarksBy || "",
          assessorRemarksDate: item.assessorRemarksDate || null,
          stage: item.stage !== undefined ? item.stage : null,
          isclsosed: item.isclsosed || "False",
          ncreply: item.ncreply || [],
          doReply: item.doReply || "0",
          doIsClosed: item.doIsClosed || "0",
          closingRemarks: item.closingRemarks || "",
        }));

        // Filter NCs by assessorId if provided in URL query
        let filteredNC = formattedNC;
        if (assessorId && assessorId.trim() !== '') {
          filteredNC = formattedNC.filter((nc) => {
            const ncAssessorId = nc.assessorid?.toString().trim();
            const urlAssessorId = assessorId?.toString().trim();
            return ncAssessorId === urlAssessorId;
          });
        }

        setNonConformities(filteredNC);

        // Show appropriate message based on filtering results
        if (assessorId && assessorId.trim() !== '' && filteredNC.length === 0 && formattedNC.length > 0) {
          showSnackbar(`No non-conformities found for assessor ID: ${assessorId}`, "info");
        } else if (filteredNC.length === 0) {
          showSnackbar("No non-conformities found for this assessment", "info");
        }
      } else {
        console.warn("❌ NC API returned false status:", response.data.message);
        showSnackbar("No non-conformities found for this assessment", "info");
      }
    } catch (error) {
      console.error("❌ Error fetching NC data:", error);
      showSnackbar("Failed to load non-conformities data", "error");
    } finally {
      setLoadingNC(false);
      setLoading(false);
    }
  }, [assessmentId, appId, assessorId, showSnackbar]);

  // Initialize data on component mount
  useEffect(() => {

    if (assessorId && assessorId.trim() !== '') {
      console.log(`🎯 Assessor filtering enabled: Will show only NCs for assessorId="${assessorId}"`);
    } else {
      console.log("📋 No assessor filtering: Will show all NCs for this assessment");
    }

    if (appId && assessmentId) {
      fetchNonConformities();
    } else {
      console.warn("⚠️ Missing required parameters:", {
        appId: !!appId,
        assessmentId: !!assessmentId,
      });
      setLoading(false);
      showSnackbar("Missing required parameters for NC data", "error");
    }
  }, [appId, assessmentId, assessorId, orgId, fetchNonConformities]);

  // Handle image lightbox
  const handleOpenLightbox = useCallback((imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setLightboxOpen(false);
    setLightboxImage("");
  }, []);

  // Handle file opening with S3 utils
  const handleFileOpen = useCallback(async (fileKey, fileName = 'document') => {
    try {
      const success = await s3Utils.handleFileViewWithS3(fileKey, {
        showError: (msg) => showSnackbar(msg, 'error'),
        showLoading: () => setLoading(true),
        hideLoading: () => setLoading(false),
      });
      
      if (!success) {
        showSnackbar(`Failed to open ${fileName}`, 'error');
      }
    } catch (error) {
      console.error('Error opening file:', error);
      showSnackbar(`Failed to open ${fileName}: ${error.message}`, 'error');
    }
  }, [showSnackbar]);

  // Helper function to check if NC action buttons should be shown
  const shouldShowActionButtons = useCallback((nc) => {
    // Don't show if NC is closed
    if (nc.doIsClosed === "1" || nc.doIsClosed === "True" || nc.doIsClosed === true) {
      return false;
    }

    // If replies exist, hide buttons when last reply is from DO (waiting for response)
    if (nc.ncreply && nc.ncreply.length > 0) {
      const sortedReplies = [...nc.ncreply].sort((a, b) => new Date(a.insertdate) - new Date(b.insertdate));
      const lastReply = sortedReplies[sortedReplies.length - 1];
      if (lastReply.replyby === 'DO') {
        return false;
      }
    }

    return true;
  }, []);

  // Reply Dialog Handlers
  const handleOpenReplyDialog = useCallback((nc, action = "reply") => {
    setSelectedNcForReply(nc);
    setReplyAction(action);
    setReplyRecipient("");
    setReplyRemarks("");
    setReplyDialogOpen(true);
  }, []);

  const handleCloseReplyDialog = useCallback(() => {
    setReplyDialogOpen(false);
    setSelectedNcForReply(null);
    setReplyRecipient("");
    setReplyRemarks("");
    setReplyAction("");
  }, []);

  // Send Reply to Assessor or Lab
  const handleSendReply = async () => {
    if (!replyRemarks.trim() || !replyRecipient || !selectedNcForReply) {
      showSnackbar("Please fill in all required fields", "error");
      return;
    }

    try {
      setSendingReply(true);

      const payload = {
        NCId: selectedNcForReply.id,
        ReplyRemarks: replyRemarks.trim(),
        ReplyById: orgId || "DO", // Use orgId for DO replies
        CorrectiveAction: "",
        CorrectiveActionupload: "",
        rootcause: "",
        rootcauseupload: "",
        Replyby: "DO", // DO is sending the reply
        replyto: replyRecipient, // "Assessor" or "Lab"
        replytoId: replyRecipient === "Assessor" ? assessorId : orgId // assessorId for Assessor, orgId for Lab
      };

      const url = `${process.env.REACT_APP_API_URL}/nc-main/ncreply`;
      const response = await axios.post(url, payload);

      if (response.data.isstatus) {
        // If this is a "close" action, update the NC status
        if (replyAction === "close") {
          try {
            const updateStatusPayload = {
              Ncid: selectedNcForReply.id,
              Status: "1", // Close the NC
              stage: "2",
            };

            const updateStatusUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Updatencstatus?${new URLSearchParams(updateStatusPayload).toString()}`;
            const updateResponse = await axios.put(updateStatusUrl);

            if (updateResponse.data.isstatus) {
              showSnackbar(`Reply sent to ${replyRecipient} and NC closed successfully`, "success");
            } else {
              showSnackbar(`Reply sent to ${replyRecipient} but failed to close NC`, "warning");
            }
          } catch (statusError) {
            console.error("❌ Error updating NC status:", statusError);
            showSnackbar(`Reply sent to ${replyRecipient} but failed to close NC`, "warning");
          }
        } else {
          showSnackbar(`Reply sent to ${replyRecipient} successfully`, "success");
        }

        handleCloseReplyDialog();
        // Refresh NC data
        fetchNonConformities();
      } else {
        showSnackbar(response.data.message || "Failed to send reply", "error");
      }
    } catch (error) {
      console.error("❌ Error sending reply:", error);
      showSnackbar("Error sending reply", "error");
    } finally {
      window.location.reload()
      setSendingReply(false);
    }
  };

  // Close NC function
  const handleCloseNC = async (nc) => {
    setSelectedNCForClose(nc);
    setCloseNCRemarks("");
    setCloseNCDialogOpen(true);
  };

  // Handle Close NC Dialog actions
  const handleCloseNCDialogClose = () => {
    setCloseNCDialogOpen(false);
    setSelectedNCForClose(null);
    setCloseNCRemarks("");
  };

  // Confirm Close NC with remarks
  const handleConfirmCloseNC = async () => {
    if (!closeNCRemarks.trim()) {
      showSnackbar("Please enter closing remarks", "error");
      return;
    }

    try {
      setClosingNC(prev => ({ ...prev, [selectedNCForClose.id]: true }));

      // Use the new NC close API with remarks
      const payload = {
        ncId: selectedNCForClose.id,
        remarks: closeNCRemarks.trim()
      };

      const url = `${process.env.REACT_APP_API_URL}/nc-main/close-nc`;
      const response = await axios.post(url, payload);

      if (response.data.isstatus) {
        showSnackbar("NC closed successfully", "success");
        // Refresh NC data
        fetchNonConformities();
        handleCloseNCDialogClose();
      } else {
        showSnackbar(response.data.message || "Failed to close NC", "error");
      }
    } catch (error) {
      console.error("❌ Error closing NC:", error);
      showSnackbar("Error closing NC", "error");
    } finally {
      window.location.reload()
      setClosingNC(prev => ({ ...prev, [selectedNCForClose.id]: false }));
    }
  };

  // Send all NCs to Lab
  const handleSendToLab = async () => {
    if (nonConformities.length === 0) {
      showSnackbar("No non-conformities to send", "warning");
      return;
    }

    try {
      setSendingToLab(true);

      // Update all NCs to set doReply = '1'
      const ncIds = nonConformities.map(nc => nc.id);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/nc-main/sendAllNCsToLab`,
        {
          ncIds: ncIds,
          appId: appId,
          assessmentId: assessmentId,
          orgId: orgId
        }
      );

      if (response.data.success) {
        showSnackbar("All non-conformities sent to Lab successfully", "success");
        // Refresh NC data
        fetchNonConformities();
      } else {
        showSnackbar(response.data.message || "Failed to send NCs to Lab", "error");
      }
    } catch (error) {
      console.error("❌ Error sending NCs to Lab:", error);
      showSnackbar("Error sending non-conformities to Lab", "error");
    } finally {
      setSendingToLab(false);
    }
  };

  // NC Reply Trail Display Component
  const NCReplyTrail = ({ nc }) => {
    if (!nc.ncreply || nc.ncreply.length === 0) {
      return null;
    }

    // Sort replies by date (oldest first)
    const sortedReplies = nc.ncreply.sort((a, b) => new Date(a.insertdate) - new Date(b.insertdate));

    return (
      <Accordion sx={{ boxShadow: 'none', border: 'none' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: alpha(colors.primary.main, 0.05),
            '&:hover': {
              bgcolor: alpha(colors.primary.main, 0.1)
            },
            borderRadius: 2,
            mb: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="subtitle2" fontWeight={600} color="primary.main">
              NC COMMUNICATION TRAIL ({sortedReplies.length} entries)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ position: 'relative' }}>
            {sortedReplies.map((reply, replyIndex) => (
              <Box
                key={reply.id || replyIndex}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  position: 'relative'
                }}
              >
                {/* Timeline Dot */}
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: reply.replyby === 'Lab' || reply.replyby === 'Cab'
                        ? colors.success.main
                        : reply.replyby === 'Assessor'
                          ? colors.primary.main
                          : colors.secondary.main, // DO
                      border: '2px solid white',
                      boxShadow: 2
                    }}
                  >
                    {reply.replyby === 'Lab' || reply.replyby === 'Cab' ? (
                      <BusinessIcon sx={{ fontSize: 16, color: 'white' }} />
                    ) : reply.replyby === 'Assessor' ? (
                      <PersonIcon sx={{ fontSize: 16, color: 'white' }} />
                    ) : (
                      <GavelIcon sx={{ fontSize: 16, color: 'white' }} />
                    )}
                  </Avatar>
                  {/* Timeline Connector */}
                  {replyIndex < sortedReplies.length - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: '50%',
                        top: 32,
                        width: 2,
                        height: 40,
                        bgcolor: colors.grey[300],
                        transform: 'translateX(-50%)',
                        zIndex: 0
                      }}
                    />
                  )}
                </Box>

                {/* Timeline Content */}
                <Box sx={{ flex: 1, pt: 0.5 }}>
                  <Card
                    elevation={1}
                    sx={{
                      p: 2,
                      bgcolor: reply.replyby === 'Lab' || reply.replyby === 'Cab'
                        ? alpha(colors.success.main, 0.05)
                        : reply.replyby === 'Assessor'
                          ? alpha(colors.primary.main, 0.05)
                          : alpha(colors.secondary.main, 0.05),
                      border: `1px solid ${reply.replyby === 'Lab' || reply.replyby === 'Cab'
                          ? colors.success.main
                          : reply.replyby === 'Assessor'
                            ? colors.primary.main
                            : colors.secondary.main
                        }`
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Chip
                        label={reply.replyby}
                        size="small"
                        color={
                          reply.replyby === 'Lab' || reply.replyby === 'Cab'
                            ? 'success'
                            : reply.replyby === 'Assessor'
                              ? 'primary'
                              : 'secondary'
                        }
                        sx={{ fontWeight: 600 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(reply.insertdate).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </Typography>
                    </Box>

                    {reply.replyRemarks && (
                      <Typography variant="body2" sx={{ mb: 2, color: colors.grey[800] }}>
                        <strong>Reply:</strong> {reply.replyRemarks}
                      </Typography>
                    )}

                    {reply.correctiveAction && (
                      <Typography variant="body2" sx={{ mb: 1, color: colors.grey[700] }}>
                        <strong>Corrective Action:</strong> {reply.correctiveAction}
                      </Typography>
                    )}

                    {reply.rootcause && (
                      <Typography variant="body2" sx={{ color: colors.grey[700] }}>
                        <strong>Root Cause:</strong> {reply.rootcause}
                      </Typography>
                    )}

                    {/* Show file attachments if any */}
                    {(reply.rootcauseupload || reply.correctiveActionupload) && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          Attachments:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          {reply.rootcauseupload && (
                            <>
                              {reply.rootcauseupload.split('~').filter(Boolean).map((doc, docIndex) => (
                                <Chip
                                  key={`rootcause-${docIndex}`}
                                  label={`Root Cause Doc ${docIndex + 1}`}
                                  size="small"
                                  onClick={() => handleFileOpen(doc, `Root Cause Document ${docIndex + 1}`)}
                                  clickable
                                  color="warning"
                                  sx={{ fontWeight: 600 }}
                                />
                              ))}
                            </>
                          )}
                          {reply.correctiveActionupload && (
                            <>
                              {reply.correctiveActionupload.split('~').filter(Boolean).map((doc, docIndex) => (
                                <Chip
                                  key={`corrective-${docIndex}`}
                                  label={`Corrective Action Doc ${docIndex + 1}`}
                                  size="small"
                                  onClick={() => handleFileOpen(doc, `Corrective Action Document ${docIndex + 1}`)}
                                  clickable
                                  color="success"
                                  sx={{ fontWeight: 600 }}
                                />
                              ))}
                            </>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Card>
                </Box>
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };
  
  

  // Working Space Document Component
  const WorkingSpaceDocument = React.memo(({
    doc,
    index,
    isImage,
    isPdf,
    isExcel,
    colors,
    onImageClick,
  }) => {
    // State for individual document image URL
    const [documentImageUrl, setDocumentImageUrl] = useState('');
    const [documentLoading, setDocumentLoading] = useState(false);
    
    // Check if doc is already a full URL or just a filename
    useEffect(() => {
      const loadDocument = async () => {
        try {
          setDocumentLoading(true);
          if (doc) {
            // Check if it's just a filename or full URL
            if (s3Utils.isJustFilename(doc)) {
              const signedUrl = await s3Utils.generateSignedUrlForFilename(doc);
              setDocumentImageUrl(signedUrl);
            } else {
              setDocumentImageUrl(doc);
            }
          }
        } catch (error) {
          console.error("Error loading document:", error);
          setDocumentImageUrl("");
        } finally {
          setDocumentLoading(false);
        }
      };
  
      loadDocument();
    }, [doc]);
    
    // Fallback URL construction
    const imageUrl = documentImageUrl || (doc?.startsWith('http') ? doc : `https://nabl7t.s3.ap-south-1.amazonaws.com/live/${doc}`);

    // For PDFs and Excel files, show an icon
    if (isPdf || isExcel) {
      return (
        <Box
          sx={{
            width: 80,
            height: 80,
            border: `2px solid ${colors.grey[300]}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s",
            "&:hover": {
              borderColor: colors.primary.main,
              transform: "scale(1.05)",
              boxShadow: 3,
            },
          }}
          onClick={() => window.open(imageUrl, "_blank")}
        >
          {isPdf ? (
            <PictureAsPdfIcon sx={{ fontSize: 24, color: colors.error.main }} />
          ) : (
            <DescriptionIcon sx={{ fontSize: 24, color: colors.success.main }} />
          )}
          <Typography
            variant="caption"
            sx={{ mt: 0.5, fontSize: "0.6rem", textAlign: "center" }}
          >
            {isPdf ? "PDF" : "Excel"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.40rem" }}
          >
            Click to view
          </Typography>
        </Box>
      );
    }

    // For images, show the actual image
    if (documentLoading) {
      return (
        <Box
          sx={{
            width: 80,
            height: 80,
            border: `2px solid ${colors.grey[300]}`,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={20} />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 0.2s",
          position: "relative",
          "&:hover": {
            borderColor: colors.primary.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() =>
          onImageClick ? onImageClick(imageUrl) : window.open(imageUrl, "_blank")
        }
      >
        <img
          src={imageUrl}
          alt={`Evidence ${index + 1}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#999;font-size:12px;">Image Error</div>';
          }}
        />
        {/* Overlay label */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "rgba(0, 0, 0, 0.7)",
            color: "white",
            py: 0.25,
            textAlign: "center",
          }}
        >
          <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 600 }}>
            Doc {index + 1}
          </Typography>
        </Box>
      </Box>
    );
  });
   

  // Main render
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          gap: 2,
        }}
      >
        <LinearProgress sx={{ width: "100%", maxWidth: "400px" }} />
        <Typography variant="body2" color="text.secondary">
          Loading Non-Conformities...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${alpha(
          colors.primary.main,
          0.02
        )} 0%, ${alpha(colors.secondary.main, 0.02)} 100%)`,
        pt: 2,
        pb: 4,
      }}
    >
      <PageTitle title="NABL | DO - Non-Conformities Review" />

      {/* Header Information */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
          background: "white",
          border: `1px solid ${colors.grey[200]}`,
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <AssignmentIcon sx={{ color: colors.primary.main }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: colors.grey[900] }}
              >
                Non-Conformities Review - DO Dashboard
              </Typography>
              <Chip
                label={`${nonConformities.length} Non-Conformities`}
                color={(assessorId && assessorId.trim() !== '') ? "secondary" : "primary"}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          }
          sx={{
            background: `linear-gradient(135deg, ${alpha(
              colors.primary.main,
              0.05
            )}, ${alpha(colors.secondary.main, 0.05)})`,
          }}
        />
      </Card>

      {/* Non-Conformities List */}
      <Box>
        {loadingNC ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Loading non-conformities data...
            </Typography>
          </Box>
        ) : nonConformities.length === 0 ? (
          <Card
            sx={{
              textAlign: "center",
              py: 6,
              borderRadius: 3,
              border: `2px dashed ${colors.grey[300]}`,
            }}
          >
            <InfoIcon
              sx={{ fontSize: 48, color: colors.grey[400], mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Non-Conformities Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This assessment has no non-conformities to review.
            </Typography>
          </Card>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {nonConformities.map((nc, index) => (
              <Paper
                key={nc.id}
                elevation={2}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  border: `1px solid ${colors.grey[200]}`,
                  transition: "all 0.2s",
                  "&:hover": {
                    boxShadow: `0 8px 25px ${alpha(colors.primary.main, 0.15)}`,
                    borderColor: colors.primary.main,
                  },
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    background: `linear-gradient(135deg, ${alpha(
                      colors.error.main,
                      0.05
                    )}, ${alpha(colors.primary.main, 0.05)})`,
                    borderBottom: `1px solid ${colors.grey[200]}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: colors.grey[900],
                      }}
                    >
                      {`NC${String(index + 1).padStart(3, "0")}`}
                    </Typography>
                    <Chip
                      label={`Clause ${nc.clause}`}
                      size="small"
                      sx={{
                        bgcolor: alpha(colors.primary.main, 0.1),
                        color: colors.primary.main,
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  {nc.description && (
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.grey[800],
                        fontWeight: 500,
                        p: 2,
                        mb: 2,
                        backgroundColor: colors.grey[50],
                        borderRadius: 2,
                        borderLeft: `4px solid ${colors.error.main}`,
                      }}
                    >
                      {nc.description}
                    </Typography>
                  )}

                  {nc.nonconformity && (
                    <Typography
                      variant="body2"
                      sx={{ color: colors.grey[700], mb: 1 }}
                    >
                      <strong>Non-Conformity:</strong> {nc.nonconformity}
                    </Typography>
                  )}

                  {nc.requirement && (
                    <Typography
                      variant="body2"
                      sx={{ color: colors.grey[700], mb: 1 }}
                    >
                      <strong>Requirement:</strong> {nc.requirement}
                    </Typography>
                  )}
                </Box>

                {/* Evidence Section */}
                {nc.evidence && nc.evidence.trim() && (
                  <Box sx={{ p: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: colors.grey[700],
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <DescriptionIcon fontSize="small" />
                      Supporting Evidence:
                    </Typography>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: 1.5,
                        maxWidth: "100%",
                      }}
                    >
                      {nc.evidence
                        .split("~")
                        .filter(Boolean)
                        .map((doc, docIndex) => {
                          const docLower = doc.toLowerCase();
                          const isImage = docLower.match(
                            /\.(jpg|jpeg|png|gif|webp)$/
                          );
                          const isPdf = docLower.endsWith(".pdf");
                          const isExcel = docLower.match(/\.(xlsx|xls)$/);

                          return (
                            <WorkingSpaceDocument
                              key={docIndex}
                              doc={doc}
                              index={docIndex}
                              isImage={isImage}
                              isPdf={isPdf}
                              isExcel={isExcel}
                              colors={colors}
                              onImageClick={
                                isImage ? handleOpenLightbox : null
                              }
                            />
                          );
                        })}
                    </Box>
                  </Box>
                )}

                {/* Laboratory Reply Section */}
                {nc.cabReply && (
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: alpha(colors.success.main, 0.05),
                      borderTop: `1px solid ${colors.grey[200]}`,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: colors.success.dark,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <ReplyIcon fontSize="small" />
                      Laboratory Reply:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.grey[800],
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${colors.grey[200]}`,
                      }}
                    >
                      {nc.cabReply}
                    </Typography>
                    {nc.cabReplyDate && (
                      <Typography
                        variant="caption"
                        sx={{ color: colors.grey[600], mt: 1, display: "block" }}
                      >
                        Reply Date: {new Date(nc.cabReplyDate).toLocaleDateString()}
                      </Typography>
                    )}

                    {/* Lab Acceptance Status */}
                    {nc.isLabAccept !== undefined && (
                      <Box sx={{ mt: 2 }}>
                        <Chip
                          label={
                            nc.isLabAccept === "1"
                              ? "Laboratory Accepted NC"
                              : "Laboratory Did Not Accept NC"
                          }
                          color={nc.isLabAccept === "1" ? "success" : "error"}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {/* Assessor Remarks Section (if available) */}
                {nc.assessorRemarks && (
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: alpha(colors.primary.main, 0.05),
                      borderTop: `1px solid ${colors.grey[200]}`,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: colors.primary.dark,
                      }}
                    >
                      Assessor Remarks:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.grey[800],
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${colors.grey[200]}`,
                      }}
                    >
                      {nc.assessorRemarks}
                    </Typography>
                    {nc.assessorRemarksBy && (
                      <Typography
                        variant="caption"
                        sx={{ color: colors.grey[600], mt: 1, display: "block" }}
                      >
                        By: {nc.assessorRemarksBy}
                        {nc.assessorRemarksDate &&
                          ` on ${new Date(nc.assessorRemarksDate).toLocaleDateString()}`
                        }
                      </Typography>
                    )}
                  </Box>
                )}

                {/* NC Reply Trail/History */}
                {nc.ncreply && nc.ncreply.length > 0 && (
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: alpha(colors.grey[50], 0.5),
                      borderTop: `1px solid ${colors.grey[200]}`,
                    }}
                  >
                    <NCReplyTrail nc={nc} />
                  </Box>
                )}

                {/* DO Action Buttons Section */}
                {shouldShowActionButtons(nc) && (
                  <Box
                    sx={{
                      p: 3,
                      backgroundColor: alpha(colors.grey[100], 0.5),
                      borderTop: `1px solid ${colors.grey[200]}`,
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: colors.grey[600], mr: 2 }}
                    >
                      DO Actions:
                    </Typography>

                    <Tooltip title="Send Clarification to Assessor or Lab">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SendIcon />}
                        onClick={() => handleOpenReplyDialog(nc, "reply")}
                        sx={{
                          background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                          color: "white",
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          px: 2,
                          "&:hover": {
                            background: `linear-gradient(135deg, ${colors.secondary.main}, ${colors.primary.main})`,
                            transform: "translateY(-1px)",
                            boxShadow: 4,
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        Send Clarification
                      </Button>
                    </Tooltip>


                    <Tooltip title="Close NC without Clarification">
                      <Button
                        color="error"
                        size="small"
                        onClick={() => handleCloseNC(nc)}
                        disabled={closingNC[nc.id]}
                        startIcon={<CloseIcon />}
                        sx={{
                          backgroundColor: alpha(colors.error.main, 0.1),
                          "&:hover": {
                            backgroundColor: alpha(colors.error.main, 0.2),
                            transform: "scale(1.05)",
                          },
                          transition: "all 0.2s ease",
                        }}
                      >
                        {closingNC[nc.id] ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          "Close NC"
                        )}
                      </Button>
                    </Tooltip>
                  </Box>
                )}

                {/* NC Status Display */}        
                <Box
                  sx={{
                    px: 3,
                    pb: 2,
                    backgroundColor: alpha(colors.grey[100], 0.3),
                  }}
                >
                  <Chip
                    label={
                      nc.doIsClosed === "True" || nc.doIsClosed === "1" || nc.doIsClosed === true
                        ? "NC Closed"
                        : "NC Open"
                    }
                    color={
                      nc.doIsClosed === "True" || nc.doIsClosed === "1" || nc.doIsClosed === true
                        ? "success"
                        : "warning"
                    }
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  {(nc.doIsClosed === "1" && nc.closingRemarks) && (
                    <Typography
                      variant="body2"
                      sx={{ color: colors.grey[700], mb: 1, mt: 1}}
                    >
                      <strong>Closing Remark:</strong> {nc.closingRemarks}
                    </Typography>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      {/* Info Message with Send to Lab Button */}
      {nonConformities.length > 0 && !nonConformities.every(nc => nc.doReply === "1" || nc.doIsClosed === "1") && (
        <Card
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 3,
            border: `1px solid ${colors.primary.main}`,
            backgroundColor: alpha(colors.primary.main, 0.05),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
              <InfoIcon sx={{ color: colors.primary.main }} />
              <Typography
                variant="body2"
                sx={{ color: colors.primary.dark, fontWeight: 500 }}
              >
                This page provides a review of all non-conformities and laboratory replies for the DO dashboard.
                Laboratory responses and corrective actions are shown for review and monitoring purposes.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="medium"
              onClick={handleSendToLab}
              disabled={sendingToLab}
              startIcon={
                sendingToLab ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              sx={{
                background: `linear-gradient(135deg, ${colors.success.main}, ${colors.primary.main})`,
                color: "white",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                minWidth: 140,
                "&:hover": {
                  background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.success.main})`,
                  transform: "translateY(-1px)",
                  boxShadow: 4,
                },
                transition: "all 0.2s ease",
              }}
            >
              {sendingToLab ? "Sending..." : "Send All to Lab OR Assessor"}
            </Button>
          </Box>
        </Card>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Close NC Dialog */}
      <Dialog
        open={closeNCDialogOpen}
        onClose={handleCloseNCDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${colors.grey[200]}`,
          backgroundColor: alpha(colors.error.main, 0.05),
          fontWeight: 600
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CloseIcon color="error" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Close Non-Conformity
            </Typography>
            {selectedNCForClose && (
              <Chip
                label={`NC${String(nonConformities.indexOf(selectedNCForClose) + 1).padStart(3, "0")}`}
                color="error"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedNCForClose && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                NC Details
              </Typography>
              <Box sx={{ bgcolor: colors.grey[50], p: 2, borderRadius: 2, mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Clause:</strong> {selectedNCForClose.clause}
                </Typography>
                <Typography variant="body2">
                  <strong>Description:</strong> {selectedNCForClose.description}
                </Typography>
              </Box>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Closing Remarks <span style={{ color: colors.error.main }}>*</span>
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Please provide remarks for closing this NC. These remarks will be recorded in the system:
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={closeNCRemarks}
                onChange={(e) => setCloseNCRemarks(e.target.value)}
                placeholder="Enter closing remarks here..."
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover": {
                      "& > fieldset": {
                        borderColor: colors.error.main,
                      },
                    },
                    "&.Mui-focused": {
                      "& > fieldset": {
                        borderColor: colors.error.main,
                      },
                    },
                  },
                }}
              />

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Warning:</strong> This action will permanently close this NC. This action cannot be undone.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseNCDialogClose}
            variant="outlined"
            sx={{
              borderColor: colors.grey[300],
              color: colors.grey[600],
              "&:hover": {
                borderColor: colors.grey[400],
                backgroundColor: alpha(colors.grey[400], 0.1),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCloseNC}
            variant="contained"
            disabled={!closeNCRemarks.trim() || (selectedNCForClose && closingNC[selectedNCForClose.id])}
            startIcon={
              selectedNCForClose && closingNC[selectedNCForClose.id] ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CloseIcon />
              )
            }
            sx={{
              background: `linear-gradient(135deg, ${colors.error.main}, ${colors.error.dark})`,
              color: "white",
              minWidth: 120,
              "&:hover": {
                background: `linear-gradient(135deg, ${colors.error.dark}, ${colors.error.main})`,
              },
            }}
          >
            {selectedNCForClose && closingNC[selectedNCForClose.id] ? "Closing..." : "Close NC"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog
        open={replyDialogOpen}
        onClose={handleCloseReplyDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${colors.grey[200]}`,
          backgroundColor: colors.grey[50],
          fontWeight: 600
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {replyAction === "close" ? (
              <CheckCircleIcon color="success" />
            ) : (
              <ReplyIcon color="primary" />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {replyAction === "close" ? "Clarification & Close NC" : "Send Clarification"}
            </Typography>
            {selectedNcForReply && (
              <Chip
                label={`NC${String(nonConformities.indexOf(selectedNcForReply) + 1).padStart(3, "0")}`}
                color={replyAction === "close" ? "success" : "primary"}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedNcForReply && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                NC Details
              </Typography>
              <Box sx={{ bgcolor: colors.grey[50], p: 2, borderRadius: 2, mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Clause:</strong> {selectedNcForReply.clause}
                </Typography>
                <Typography variant="body2">
                  <strong>Description:</strong> {selectedNcForReply.description}
                </Typography>
              </Box>

              {/* Recipient Selection */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Send Clarification To
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Select Recipient</InputLabel>
                  <Select
                    value={replyRecipient}
                    label="Select Recipient"
                    onChange={(e) => setReplyRecipient(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      }
                    }}
                  >
                    <MenuItem value="Assessor">Assessor</MenuItem>
                    <MenuItem value="Lab">Laboratory</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Reply Message */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {replyAction === "close" ? "Closing Remarks" : "Clarification Message"}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {replyAction === "close"
                    ? "Please provide your remarks for closing this NC. This message will be sent to the selected recipient:"
                    : "Please provide your clarification message that will be sent to the selected recipient:"
                  }
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={replyRemarks}
                  onChange={(e) => setReplyRemarks(e.target.value)}
                  placeholder={`Enter your ${replyAction === "close" ? "closing remarks" : "Clarification"} here...`}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&:hover": {
                        "& > fieldset": {
                          borderColor: replyAction === "close" ? colors.success.main : colors.primary.main,
                        },
                      },
                      "&.Mui-focused": {
                        "& > fieldset": {
                          borderColor: replyAction === "close" ? colors.success.main : colors.primary.main,
                        },
                      },
                    },
                  }}
                />
              </Box>

              {replyAction === "close" && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> This action will send a reply to the selected recipient and permanently close this NC.
                    This action cannot be undone.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseReplyDialog}
            variant="outlined"
            sx={{
              borderColor: colors.grey[300],
              color: colors.grey[600],
              "&:hover": {
                borderColor: colors.grey[400],
                backgroundColor: alpha(colors.grey[400], 0.1),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendReply}
            variant="contained"
            disabled={sendingReply || !replyRemarks.trim() || !replyRecipient}
            startIcon={
              sendingReply ? (
                <CircularProgress size={16} color="inherit" />
              ) : replyAction === "close" ? (
                <CheckCircleIcon />
              ) : (
                <SendIcon />
              )
            }
            sx={{
              background: replyAction === "close"
                ? `linear-gradient(135deg, ${colors.success.main}, ${colors.success.dark})`
                : `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
              color: "white",
              minWidth: 120,
              "&:hover": {
                background: replyAction === "close"
                  ? `linear-gradient(135deg, ${colors.success.dark}, ${colors.success.main})`
                  : `linear-gradient(135deg, ${colors.secondary.main}, ${colors.primary.main})`,
              },
            }}
          >
            {sendingReply
              ? "Sending..."
              : replyAction === "close"
                ? "Reply & Close"
                : "Send Reply"
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Lightbox (if needed) */}
      {lightboxOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "pointer",
          }}
          onClick={handleCloseLightbox}
        >
          <img
            src={lightboxImage}
            alt="Evidence"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default DoNCReply;